// ============================================================
// CreateTenantDialog.js
// Modal form: name + identity provider + optional external tenant ID.
// Submit → POST /api/mgmt/admin/tenants. Closes on success.
// ============================================================

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../components/Modal';
import Field from '../components/Field';
import { createTenant } from '../api/tenants';
import { translateError } from '../utils/errorMessages';
import { toast } from '../components/Toaster';
import './CreateTenantDialog.css';

// Default is 'other' (2026-09-03): the provider is a commercial fact
// the operator checks by hand before provisioning, so choosing
// Microsoft or Google must be a deliberate act. The old m365 default
// stamped every prospect Microsoft 365 whether true or not.
const INITIAL_STATE = {
  tenantName:        '',
  identityProvider:  'other',
  externalTenantId:  '',
};

// Mirrors backend shared/tenant-identity-providers.js and the badge
// label map in StatusBadge.js — widen all together (Gotcha #105).
export const IDENTITY_PROVIDER_OPTIONS = [
  { value: 'other',            label: 'Other business email / not yet known' },
  { value: 'm365',             label: 'Microsoft 365' },
  { value: 'google_workspace', label: 'Google Workspace' },
  { value: 'consumer_email',   label: 'Personal email (Gmail, Outlook.com, Hotmail), no organisation tenant' },
];

export default function CreateTenantDialog({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  function handleClose() {
    if (isSubmitting) return;
    setForm(INITIAL_STATE);
    setErrors({});
    setSubmitError(null);
    onClose();
  }

  function validate() {
    const e = {};
    if (!form.tenantName.trim()) e.tenantName = 'Tenant name is required.';
    if (form.tenantName.length > 200) e.tenantName = 'Tenant name must be 200 characters or fewer.';
    if (!form.identityProvider) e.identityProvider = 'Identity provider is required.';
    if (form.externalTenantId && form.externalTenantId.length > 100) {
      e.externalTenantId = 'Client tenant directory (tenant) ID must be 100 characters or fewer.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        tenantName: form.tenantName.trim(),
        identityProvider: form.identityProvider,
      };
      if (form.externalTenantId.trim()) body.externalTenantId = form.externalTenantId.trim();
      const created = await createTenant(body);
      toast.success(`Tenant "${created.tenantName}" created`);
      setForm(INITIAL_STATE);
      setErrors({});
      onCreated(created);
      onClose();
    } catch (err) {
      // Field-level error if backend identifies one; otherwise top-level banner.
      if (err && err.field) {
        setErrors({ [err.field]: translateError(err) });
      } else {
        setSubmitError(translateError(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Tenant" closeDisabled={isSubmitting}>
      <form className="create-tenant-form" onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="create-tenant-form__banner" role="alert">{submitError}</div>
        )}

        <Field label="Tenant name" error={errors.tenantName} required>
          <input
            type="text"
            value={form.tenantName}
            maxLength={200}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, tenantName: e.target.value })}
            autoFocus
          />
        </Field>

        <Field
          label="Identity provider"
          error={errors.identityProvider}
          required
          help="Which email / identity system the organisation runs. Descriptive only: sign-in is email and password until federation is set up. Can be changed later from the tenant page."
        >
          <select
            value={form.identityProvider}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, identityProvider: e.target.value })}
          >
            {IDENTITY_PROVIDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Client tenant directory (tenant) ID"
          error={errors.externalTenantId}
          help="Optional. The client's M365 / Workspace directory (tenant) ID — a GUID — if known. Same concept the Federation tab refers to as 'Client tenant directory ID'; used by Workstream 3 for federation routing."
        >
          <input
            type="text"
            value={form.externalTenantId}
            maxLength={100}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, externalTenantId: e.target.value })}
          />
        </Field>

        <div className="create-tenant-form__actions">
          <button
            type="button"
            className="create-tenant-form__cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="create-tenant-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={14} className="create-tenant-form__spinner" />}
            Create tenant
          </button>
        </div>
      </form>
    </Modal>
  );
}
